/**
 * Pure function that maps a GetFileResponse to a ViewerType string.
 *
 * Pure function with no side effects and no DOM access. Used by React
 * components (e.g. PlayerSlot) to decide which viewer to render for a given
 * file.
 *
 * Branching order:
 *  1. file_type_id === 1 → 'video'
 *  2. file_type_id === 2 → 'audio'
 *  3. file_type_id === 3 → 'image'
 *  4. file_type_id === 5 → 'commentstream'
 *  5. file_type_id === 6 AND (mime_type === 'application/pdf' OR a proxy is a
 *     PDF) → 'document'. Documents such as .docx are stored with their own
 *     mime type but get a PDF proxy generated for them, so the proxies have
 *     to be consulted too.
 *  6. mime_type === 'text/plain' → 'text'
 *  7. fallback → 'download'
 */

import { pdfProxy } from './proxySelection';
import type { GetFileResponse } from '../player-loader/loadFile';

export type ViewerType =
  | 'video'
  | 'audio'
  | 'image'
  | 'document'
  | 'text'
  | 'commentstream'
  | 'download';

export function resolveViewerType(fileData: GetFileResponse): ViewerType {
  if (fileData.file_type_id === 1) {
    return 'video';
  }
  if (fileData.file_type_id === 2) {
    return 'audio';
  }
  if (fileData.file_type_id === 3) {
    return 'image';
  }
  if (fileData.file_type_id === 5) {
    return 'commentstream';
  }

  const isPdf = fileData.file_type_id === 6
    && (fileData.mime_type === 'application/pdf' || pdfProxy(fileData) !== undefined);
  if (isPdf) {
    return 'document';
  }

  if (fileData.mime_type === 'text/plain') {
    return 'text';
  }

  return 'download';
}
